import { Timestamp, collection, deleteDoc, deleteField, doc, getDoc, getDocs, getFirestore, limit, orderBy, query, QueryDocumentSnapshot, setDoc, startAfter, where, writeBatch } from "firebase/firestore";
import { database } from "../..";
import { handleAuthError, isAuthError } from "../../../utils/handleAuthError";

interface HistorialEstado {
  estado: string;
  fecha: string;
  hora: string;
}

export interface Envio {
  id: string;
  nombre: string;
  cantidadPaquetes: number;
  fechaDesde: Timestamp;
  fechaHasta: Timestamp;
  estado: string;
  historialEstados?: HistorialEstado[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export async function fetchEnviosLoteado(
  startDate: Date,
  endDate: Date,
  lastDoc?: QueryDocumentSnapshot,
): Promise<{
  envios: Envio[];
  lastDoc?: QueryDocumentSnapshot;
}> {
  const db = getFirestore();
  const enviosRef = collection(db, "envios");

  const enviosQuery = query(
    enviosRef,
    where("createdAt", ">=", startDate),
    where("createdAt", "<=", endDate),
    orderBy("createdAt", "desc"),
    ...(lastDoc ? [startAfter(lastDoc)] : []),
    limit(100)
  );

  const snapshot = await getDocs(enviosQuery);

  const envios = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...(doc.data() as Partial<Envio>),
  })) as Envio[];

  const lastVisible = snapshot.docs[snapshot.docs.length - 1];

  return {
    envios,
    lastDoc: lastVisible,
  };
}

export async function createEnvio(envio: Omit<Envio, "id" | "createdAt" | "updatedAt" | "historialEstados">) {
  try {
    const enviosRef = collection(database, "envios");
    const now = Timestamp.now();
    
    // Obtener fecha y hora de Nicaragua
    const fechaNicaragua = new Date(
      new Date().toLocaleString("en-US", { timeZone: "America/Managua" })
    );
    const fechaStr = fechaNicaragua.toISOString().split("T")[0];
    const horaStr = fechaNicaragua.toTimeString().slice(0, 5);
    
    // Crear historial inicial con el estado inicial
    const historialEstados: HistorialEstado[] = [{
      estado: envio.estado,
      fecha: fechaStr,
      hora: horaStr,
    }];
    
    const newEnvio: Omit<Envio, "id"> = {
      ...envio,
      historialEstados,
      createdAt: now,
      updatedAt: now,
    };

    const docRef = doc(enviosRef);
    await setDoc(docRef, newEnvio);

    return { id: docRef.id, ...newEnvio };
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      throw error;
    }
    console.error("Error creando envío:", error);
    throw error;
  }
}

export async function updateEnvio(id: string, envio: Partial<Envio>, oldEnvio?: Envio) {
  try {
    const docRef = doc(database, "envios", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data() as Envio;
      const nombreEnvio = data.nombre;
      const { updatePackagesInBatch } = await import("../paquetes");
      
      // Si se actualiza el nombre del envío, actualizar el campo envio de todos los paquetes
      if (envio.nombre && oldEnvio && envio.nombre !== oldEnvio.nombre) {
        const nombreEnvioAnterior = oldEnvio.nombre;
        const nombreEnvioNuevo = envio.nombre;
        
        // Obtener todos los paquetes que pertenecen al envío con el nombre anterior
        const paquetesRef = collection(database, "paquetes");
        const q = query(paquetesRef, where("envio", "==", nombreEnvioAnterior));
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
          const paqueteIds = snapshot.docs.map((doc) => doc.id);
          
          // Actualizar el campo envio de todos los paquetes con el nuevo nombre
          await updatePackagesInBatch({
            ids: paqueteIds,
            field: "envio",
            value: nombreEnvioNuevo,
          });
        }
      }
      
      // Si se actualiza el estado del envío, actualizar el estado de todos los paquetes y guardar en historial
      let historialEstados = data.historialEstados || [];
      if (envio.estado && oldEnvio && envio.estado !== oldEnvio.estado) {
        // Obtener todos los paquetes que pertenecen a este envío
        // Usar el nombre nuevo si cambió, o el nombre actual si no cambió
        const nombreEnvioActual = envio.nombre || nombreEnvio;
        const paquetesRef = collection(database, "paquetes");
        const q = query(paquetesRef, where("envio", "==", nombreEnvioActual));
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
          const paqueteIds = snapshot.docs.map((doc) => doc.id);
          
          // Mapear el estado del envío al estado del paquete (son los mismos estados)
          const estadoPaquete = envio.estado;
          
          // Actualizar el estado de todos los paquetes en batch
          await updatePackagesInBatch({
            ids: paqueteIds,
            field: "estado",
            value: estadoPaquete,
          });
        }
        
        // Agregar el nuevo estado al historial
        const fechaNicaragua = new Date(
          new Date().toLocaleString("en-US", { timeZone: "America/Managua" })
        );
        const fechaStr = fechaNicaragua.toISOString().split("T")[0];
        const horaStr = fechaNicaragua.toTimeString().slice(0, 5);
        
        historialEstados = [
          ...historialEstados,
          {
            estado: envio.estado,
            fecha: fechaStr,
            hora: horaStr,
          },
        ];
      }
      
      const updatedData = { 
        ...data, 
        ...envio, 
        historialEstados,
        updatedAt: Timestamp.now() 
      };
      await setDoc(docRef, updatedData);
      return { ...updatedData, id } as Envio;
    }
    throw new Error("Envío no encontrado");
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      throw error;
    }
    console.error("Error actualizando envío:", error);
    throw error;
  }
}

export async function getEnvioById(id: string): Promise<Envio | null> {
  try {
    const docRef = doc(database, "envios", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Envio;
    }
    return null;
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      throw error;
    }
    console.error("Error obteniendo envío:", error);
    throw error;
  }
}

export async function getEnvioByNombre(nombre: string): Promise<Envio | null> {
  try {
    const enviosRef = collection(database, "envios");
    const q = query(enviosRef, where("nombre", "==", nombre), limit(1));
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() } as Envio;
    }
    return null;
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      throw error;
    }
    console.error("Error obteniendo envío por nombre:", error);
    throw error;
  }
}

export async function updateEnvioCantidadPaquetes(nombreEnvio: string) {
  try {
    const paquetesRef = collection(database, "paquetes");
    const q = query(paquetesRef, where("envio", "==", nombreEnvio));
    const snapshot = await getDocs(q);
    const cantidadPaquetes = snapshot.size;

    // Buscar el envío por nombre
    const envio = await getEnvioByNombre(nombreEnvio);
    if (envio) {
      await updateEnvio(envio.id, { cantidadPaquetes });
    }
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      throw error;
    }
    console.error("Error actualizando cantidad de paquetes del envío:", error);
    throw error;
  }
}

export async function deleteEnvio(id: string) {
  try {
    const docRef = doc(database, "envios", id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error("Envío no encontrado");
    }

    const data = docSnap.data() as Envio;
    const nombreEnvio = data.nombre;

    // Buscar todos los paquetes con este envío asignado
    const paquetesRef = collection(database, "paquetes");
    const q = query(paquetesRef, where("envio", "==", nombreEnvio));
    const snapshot = await getDocs(q);

    // Si hay paquetes, actualizar el campo envio a null usando deleteField
    if (!snapshot.empty) {
      const batch = writeBatch(database);
      snapshot.docs.forEach((docSnap) => {
        const paqueteRef = doc(database, "paquetes", docSnap.id);
        batch.update(paqueteRef, { envio: deleteField() });
      });
      await batch.commit();
    }

    // Eliminar el documento del envío
    await deleteDoc(docRef);
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      throw error;
    }
    console.error("Error eliminando envío:", error);
    throw error;
  }
}
